import { MediaStatus, useCreateMutation, useUpdateMutation } from '../../graphql';
import { useConfig } from '../../hooks';
import { getFileDataFromUrl, getMediaTypeByExt } from '../mediaUtils';
import { MediaItem, MediaUploadArgs } from '../types';

type BaseHookArgs = {
  args: MediaUploadArgs;
  upload: (
    mediaItem: MediaItem,
    file?: File,
    fileUrl?: string
  ) => Promise<void>;
};

export type UploadMediaOptions = {
  workspaceId?: string;
  file?: File;
  name?: string;
  categories?: Array<{ id: string }>;
  overrideExtension?: string;
};

export const useMediaUpload = (args: BaseHookArgs) => {
  const { data } = useConfig();

  const { mutateAsync } = useCreateMutation({
    query: CREATE_MEDIA,
    modelName: 'media',
  });

  const { mutateAsync: updateMedia } = useUpdateMutation({
    query: UPDATE_MEDIA,
    modelName: 'media',
  });

  const uploadMedia = async (
    fileUrl: string,
    options: UploadMediaOptions = {}
  ): Promise<string | undefined> => {
    if (!data || !fileUrl) return;

    const { workspaceId, file, name, categories, overrideExtension } = options;

    const mediaItem = createMediaItem(fileUrl, overrideExtension);
    if (name) mediaItem.name = name;

    const input: any = {
      name: mediaItem.name,
      status: MediaStatus.Pending,
      type: mediaItem.type,
      extension: mediaItem.extension,
    };
    if (workspaceId) input.workspaces = [{ id: workspaceId }];
    if (categories) input.categories = categories;

    const created = await mutateAsync({ input });
    mediaItem.id = created.id;
    // fire-and-forget so the heavy blob upload doesn't block the UI
    void args.upload(mediaItem, file, fileUrl);
    return created.id;
  };

  return uploadMedia;
};

const createMediaItem = (
  fileUrl: string,
  overrideExtension?: string
): MediaItem => {
  const fileData = getFileDataFromUrl(fileUrl);
  const ext = overrideExtension ?? fileData.ext;
  const name = overrideExtension
    ? swapExtension(fileData.name, overrideExtension)
    : fileData.name;
  return {
    id: '',
    name,
    url: fileUrl,
    extension: ext,
    type: getMediaTypeByExt(ext),
    status: MediaStatus.Pending,
  };
};

const swapExtension = (fileName: string, newExt: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return `${fileName}.${newExt}`;
  return `${fileName.slice(0, lastDot)}.${newExt}`;
};

const CREATE_MEDIA = `
  mutation Mutation($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;

const UPDATE_MEDIA = `
  mutation Mutation($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;
